import { useMemo } from 'react';
import { cn } from '@/utils/cn';
import { TEXT_BG, TEXT_SIGN, TEXT_SIGN_COLOR } from './constants';
import { splitPairs, type DiffTextLine, type TextLineType, type WordSeg } from './computeTextDiff';

const Words = ({ w, kind }: { w: WordSeg[]; kind: TextLineType }) => (
  <>
    {w.map((part, i) => (
      <span
        key={i}
        data-diff-highlight={part.hl ? kind : undefined}
        className={cn(
          part.hl &&
            (kind === 'add' ? 'bg-emerald-200/80 rounded-[2px]' : 'bg-red-200/80 rounded-[2px]')
        )}
      >
        {part.s}
      </span>
    ))}
  </>
);

const lineTextColor = (t: TextLineType) =>
  t === 'del' ? 'text-red-900' : t === 'add' ? 'text-emerald-900' : 'text-neutral-700';

interface TextDiffProps {
  lines: DiffTextLine[];
  split: boolean;
  onlyChanges: boolean;
  baseLabel: string;
  targetLabel: string;
}

export const TextDiff = ({ lines, split, onlyChanges, baseLabel, targetLabel }: TextDiffProps) => {
  const visible = useMemo(
    () => (onlyChanges ? lines.filter((l) => l.t !== 'ctx') : lines),
    [lines, onlyChanges]
  );
  const pairs = useMemo(() => (split ? splitPairs(visible) : []), [split, visible]);

  if (!split) {
    return (
      <div className="font-mono text-[12px] leading-[1.6]">
        <div className="flex gap-4 border-b border-neutral-100 px-3 py-1 font-sans text-xs text-neutral-500">
          <span>− {baseLabel}</span>
          <span>+ {targetLabel}</span>
        </div>
        {visible.map((line, i) => (
          <div
            key={i}
            data-diff-change={line.t !== 'ctx' || undefined}
            className={cn('flex', TEXT_BG[line.t], line.t === 'ctx' && 'hover:bg-neutral-25')}
          >
            <span className="w-11 shrink-0 pr-2 text-right text-neutral-400 select-none border-r border-neutral-100 tabular-nums">
              {line.a ?? ''}
            </span>
            <span className="w-11 shrink-0 pr-2 text-right text-neutral-400 select-none border-r border-neutral-100 tabular-nums">
              {line.b ?? ''}
            </span>
            <span className={cn('w-5 shrink-0 text-center select-none', TEXT_SIGN_COLOR[line.t])}>
              {TEXT_SIGN[line.t]}
            </span>
            <span
              className={cn(
                'min-w-0 flex-1 pr-3 whitespace-pre-wrap break-words',
                lineTextColor(line.t)
              )}
            >
              <Words w={line.w} kind={line.t} />
            </span>
          </div>
        ))}
      </div>
    );
  }

  const Side = ({ line, side }: { line: DiffTextLine | null; side: 'a' | 'b' }) => (
    <div className={cn('flex min-w-0', line ? TEXT_BG[line.t] : 'bg-neutral-25/60')}>
      <span className="w-10 shrink-0 pr-2 text-right text-neutral-400 select-none border-r border-neutral-100 tabular-nums">
        {line ? (side === 'a' ? line.a : line.b) ?? '' : ''}
      </span>
      <span className={cn('w-4 shrink-0 text-center select-none', line && TEXT_SIGN_COLOR[line.t])}>
        {line ? TEXT_SIGN[line.t] : ''}
      </span>
      <span
        className={cn(
          'min-w-0 flex-1 pr-3 whitespace-pre-wrap break-words',
          line && lineTextColor(line.t)
        )}
      >
        {line ? <Words w={line.w} kind={line.t} /> : ''}
      </span>
    </div>
  );

  return (
    <div className="font-mono text-[12px] leading-[1.6]">
      <div className="grid grid-cols-2 h-6 items-center text-[10.5px] font-sans font-semibold uppercase tracking-wider text-neutral-500 border-b border-neutral-100">
        <span className="px-3 border-r border-neutral-150">{baseLabel}</span>
        <span className="px-3">{targetLabel}</span>
      </div>
      {pairs.map(([a, b], i) => (
        <div
          key={i}
          data-diff-change={(a && a.t !== 'ctx') || (b && b.t !== 'ctx') || undefined}
          className="grid grid-cols-2"
        >
          <div className="border-r border-neutral-150 min-w-0">
            <Side line={a} side="a" />
          </div>
          <div className="min-w-0">
            <Side line={b} side="b" />
          </div>
        </div>
      ))}
    </div>
  );
};
